import { isBefore } from "date-fns";
import { z } from "zod";

import { monthsValues, OTHER_VALUE } from "~/common/constants";
import { usaStates } from "~/common/constants/usaStates";
import { DEFAULT_CODE_LENGTH } from "~/common/constants/verificationCode";
import {
  namesAndEmailSchema,
  termsAndConditionsSchema,
} from "~/common/utils/signUpSchemas";
import { PATIENT_SIGN_UP_MIN_AGE, PRONOUNS } from "../constants";
import { relationshipValues } from "../constants/relationships";
import { isOlderThanYears } from "./isOlderThanYears";
import { pharmacySchema } from "./pharmacySchema";

export const namesSchema = z.object({
  name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
});

export const userMobileSchema = z.object({
  phone_number: z
    .string()
    .min(1, "Mobile number required")
    .regex(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, {
      error: "Please enter mobile number with format: (123) 456-7890",
    }),
});

export const userMobileVerificationSchema = z.object({
  code: z
    .string({ error: "Verification code is required" })
    .min(6, `${DEFAULT_CODE_LENGTH} character code required`),
});

export const patientBirthdateSchema = z.object({
  birthday: z
    .string()
    .min(1, { error: `Date of birth is required.` })
    .refine((val) => isOlderThanYears(val, PATIENT_SIGN_UP_MIN_AGE), {
      error: `PsychNow does not support users under the age of ${PATIENT_SIGN_UP_MIN_AGE}`,
    })
    .refine((dateString) => isBefore(new Date(dateString), new Date()), {
      error: "Please enter a valid date of birth",
    }),
  month: z.enum(monthsValues, {
    error: "Please select an option",
  }),
  day: z.string().min(1, "Day is required"),
  year: z.string().min(1, "Year is required"),
});

export const patientAddressSchema = z.object({
  street: z.string().min(1, "Street is required"),
  apartment: z.string(),
  city: z.string().min(1, "City is required"),
  state: z.enum(usaStates, {
    error: "State is required",
  }),
  zipcode: z.string().length(5, "ZIP Code must have 5 digits."),
});

export const emergencyContactSchema = z
  .object({
    name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Last name is required"),
    relationship: z.enum(relationshipValues, {
      error: "Please select an option",
    }),
    customRelationship: z.string().optional(),
    email: z.email("Please provide a valid email address."),
    phone_number: z
      .string()
      .min(1, "Mobile number required")
      .regex(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, {
        error: "Please enter mobile number with format: (123) 456-7890",
      }),
  })
  .refine(
    (data) => {
      const valid = !(
        (data.relationship === OTHER_VALUE && !data.customRelationship) ||
        (data.customRelationship && /\d/.test(data.customRelationship))
      );
      return valid;
    },
    {
      path: ["customRelationship"],
      error: "Please enter a relationship",
    },
  );

export const passwordSchema = z
  .object({
    password: z.string(),
    passwordConfirmation: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    path: ["passwordConfirmation"],
    error:
      "Passwords don't match. Please make sure to enter the same password in both fields.",
  })
  .refine(
    (data) => {
      if (data.password !== data.passwordConfirmation) return true;
      const passwordRegex =
        // eslint-disable-next-line  no-useless-escape
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?~ ])[A-Za-z\d`!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?~ ]{8,}$/;
      const valid = passwordRegex.test(data.password);
      return valid;
    },
    {
      path: ["password"],
      error: "Passwords don't meet the requirements.",
    },
  );

export const pronounsSchema = z
  .object({
    pronouns: z.string({
      error: "Please select an option",
    }),
    customPronouns: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.pronouns !== PRONOUNS.other) return true;
      const valid =
        data.customPronouns && /^\w+\/\w+\/\w+$/.test(data.customPronouns);
      return valid;
    },
    {
      path: ["customPronouns"],
      error: "Enter 3 pronouns separated by '/'",
    },
  );

export const PATIENTS_TYPES = {
  newPatient: "newPatient",
  activePatient: "activePatient",
} as const;

const signUpPatientBaseSchema = z.object({
  pronouns: pronounsSchema,
  phone_number: userMobileSchema,
  address: patientAddressSchema,
  emergencyContact: emergencyContactSchema,
  password: passwordSchema,
});

const signUpNewPatientBaseSchema = z.object({
  date_of_birth: patientBirthdateSchema,
  address: patientAddressSchema,
  phone_number: userMobileSchema,
  verification: userMobileVerificationSchema,
  password: passwordSchema,
});

export const newPatientDataSchema = signUpNewPatientBaseSchema.extend({
  type: z.literal(PATIENTS_TYPES.newPatient),
  namesAndEmail: namesAndEmailSchema,
  termsAndConditions: termsAndConditionsSchema,
});

export const patientDataSchema = signUpPatientBaseSchema.extend({
  type: z.literal(PATIENTS_TYPES.activePatient),
  termsAndConditions: termsAndConditionsSchema,
  names: namesSchema,
  pharmacy: pharmacySchema,
});

export const signUpPatientSchema = z.discriminatedUnion("type", [
  newPatientDataSchema,
  patientDataSchema,
]);

export type SignUpPatientSchemaType = z.infer<typeof signUpPatientSchema>;
export type NewPatientSchemaType = z.infer<typeof newPatientDataSchema>;
export type ActivePatientSchemaType = z.infer<typeof patientDataSchema>;
export type AccountSchemaType = z.infer<typeof updatePatientUserDetailsSchema>;

export const updatePatientAccountAccessSchema = z.object({
  email: z.email("Please enter a valid email address"),
  phone_number: z
    .string()
    .min(1, "Mobile number required")
    .regex(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, {
      error: "Please enter mobile number with format: (123) 456-7890",
    }),
});

export const updatePatientUserDetailsSchema = z.object({
  name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  pronouns: pronounsSchema,
  birthday: z
    .string({ error: "Please provide your date of birth" })
    .refine((dateString) => isBefore(new Date(dateString), new Date()), {
      error: "Please enter a valid date of birth",
    }),
  addresses: z.array(patientAddressSchema),
  emergency_contacts: z.array(emergencyContactSchema),
});

export const loginUserSchema = z.object({
  email: z
    .email({ error: "Please enter a valid email address." })
    .min(1, { error: "Email is required" }),
  password: z.string().min(1, { error: "Password is required" }),
});

export const emailSchema = z.object({
  email: z
    .email({ error: "Please enter a valid email address." })
    .min(1, { error: "Email is required" }),
});

export type NamesSchemaType = z.infer<typeof namesSchema>;
export type PronounsSchemaType = z.infer<typeof pronounsSchema>;
export type UserBirthdateSchemaType = z.infer<typeof patientBirthdateSchema>;
export type UserMobileSchemaType = z.infer<typeof userMobileSchema>;
export type UserMobileVerificationSchemaType = z.infer<
  typeof userMobileVerificationSchema
>;
export type AddressSchemaType = z.infer<typeof patientAddressSchema>;
export type EmergencyContactSchemaType = z.infer<typeof emergencyContactSchema>;
export type PatientSchemaType = z.infer<typeof patientDataSchema>;
